import React, { forwardRef, useMemo } from 'react'
import styled from 'styled-components'
import { TextTooltip } from '../InfoPopup'
import { useScreen } from '@root/utils'
import { Button } from '../Button'
import { FaCheck, FaPlus, FaTimes } from 'react-icons/fa'
import theme from '@root/styles/theme'
import cn from 'classnames'
import type { IconType } from 'react-icons'

export interface TagPillTag<TId extends string | number = number> {
  id: TId
  name: string
  description?: string
  color?: string
}

interface PropsTagPill<TId extends string | number = number> {
  tag: TagPillTag<TId>
  icon?: IconType
  onDisMiss?: (tagId: TId) => void
  focusAfterDismiss?: React.RefObject<HTMLElement>
  maxWidth?: string | number
  onClick?: () => void
  isSelected?: boolean
  isRemoved?: boolean
  disableHoverText?: boolean
}

function TagPillInner<TId extends string | number = number>({
  tag,
  icon: Icon,
  onDisMiss,
  focusAfterDismiss,
  maxWidth,
  onClick,
  isSelected,
  isRemoved,
  disableHoverText = false
}: PropsTagPill<TId>) {
  const { isMobile } = useScreen()

  const tooltip = useMemo(() => {
    let tooltipText = tag.name
    if (tag.description) {
      tooltipText += `\n\n${tag.description}`
    }
    return tooltipText
  }, [tag.name, tag.description])

  const handleDismiss = () => {
    onDisMiss?.(tag.id)
    focusAfterDismiss?.current?.focus()
  }

  const wrapWithTooltipIfNeeded = (content: React.ReactNode): React.ReactNode => {
    if (disableHoverText) {
      return content
    }
    return (
      <TextTooltip
        tooltip={tooltip}
        placement="bottom-start"
        closeDelay={onClick ? 200 : undefined}
      >
        {content}
      </TextTooltip>
    )
  }

  const pillContent = (
    <StyledPill
      $color={tag.color ?? theme.colors.metalGray}
      $isMobile={isMobile}
      $maxWidth={maxWidth}
      className={cn(isSelected && 'selected', isRemoved && 'removed')}
    >
      {Icon ? (
        <StyledRoleIcon>
          <Icon />
        </StyledRoleIcon>
      ) : (
        <StyledColorDot $color={tag.color ?? theme.colors.metalGray} />
      )}
      <StyledName $color={tag.color}>{tag.name}</StyledName>
      {onDisMiss && (
        <StyledButton
          type="button"
          $color={tag.color}
          isSecondary
          icon={<FaTimes />}
          onClick={handleDismiss}
        />
      )}
      {isSelected && <StyledCheckIcon />}
    </StyledPill>
  )

  if (onClick) {
    return wrapWithTooltipIfNeeded(
      <StyledNoStyleButton
        type="button"
        onClick={e => {
          e.stopPropagation()
          e.preventDefault()
          onClick()
        }}
      >
        {pillContent}
      </StyledNoStyleButton>
    )
  }

  return wrapWithTooltipIfNeeded(pillContent)
}

export const TagPill = React.memo(TagPillInner) as typeof TagPillInner

interface PropsCreateNewTagPill {
  onClick: () => void
  text?: string
}

export const CreateTagPill = forwardRef<HTMLButtonElement, PropsCreateNewTagPill>(
  ({ onClick, text }, ref) => {
    const { isMobile } = useScreen()

    const color = theme.colors.metalGray

    return (
      <StyledNoStyleButton
        ref={ref}
        type="button"
        onClick={e => {
          e.stopPropagation()
          e.preventDefault()
          onClick()
        }}
      >
        <StyledPill $color={color} $isMobile={isMobile} className={cn('create-tag-pill')}>
          <StyledPlusIcon $color={color} $hasText={!!text} />
          {text && <StyledName $color={color}>{text}</StyledName>}
        </StyledPill>
      </StyledNoStyleButton>
    )
  }
)

const StyledCheckIcon = styled(FaCheck)`
  color: ${({ theme }) => theme.colors.neutralWhite};
  margin-left: ${({ theme }) => theme.spacing.xxs}rem;
  width: ${({ theme }) => theme.fontSize.xs}rem;
  height: ${({ theme }) => theme.fontSize.xs}rem;
`

const StyledPlusIcon = styled(FaPlus)<{ $color: string; $hasText: boolean }>`
  color: ${({ $color }) => $color};
  margin-right: ${({ theme, $hasText }) => ($hasText ? `${theme.spacing.xxs}rem` : 0)};
  width: ${({ theme }) => theme.fontSize.xs}rem;
  height: ${({ theme }) => theme.fontSize.xs}rem;
  flex-shrink: 0;
`

const StyledNoStyleButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary} !important;
    border-radius: ${({ theme }) => theme.borderRadius.sm}rem !important;
  }
`

const StyledName = styled.p<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 500;
  line-height: 1.25;
  margin: 0 !important;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  max-width: 100%;
`

const StyledPill = styled.div<{
  $color: string
  $isMobile: boolean
  $maxWidth?: string | number
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  min-height: 22px !important;
  padding: ${({ theme }) => theme.spacing.xxs}rem ${({ theme }) => theme.spacing.xs}rem;
  background-color: ${({ theme }) => theme.colors.neutralWhite};
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  overflow: hidden;
  flex-wrap: nowrap;
  box-sizing: border-box;

  &.create-tag-pill {
    border-style: dashed;
    border-color: ${({ theme }) => theme.colors.metalGray}66;
  }

  &.selected {
    background-color: ${({ $color }) => $color};
    border-color: transparent;
  }

  &.selected ${StyledName} {
    color: ${({ theme }) => theme.colors.neutralWhite};
  }

  ${StyledName} {
    color: ${({ theme }) => theme.colors.metalGray};
  }

  &.removed {
    text-decoration: line-through;
    text-decoration-color: ${({ theme }) => theme.colors.iconGray};
  }
`

const StyledButton = styled(Button)<{ $color: string }>`
  color: ${({ theme }) => theme.colors.metalGray} !important;
  margin: -4px;
  padding: 4px !important;
  transition: unset !important;
  min-height: 22px !important;

  & svg {
    width: ${({ theme }) => theme.fontSize.xs}rem !important;
    height: ${({ theme }) => theme.fontSize.xs}rem !important;
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary} !important;
    outline-offset: 1px !important;
    background-color: unset !important;
  }
  &:hover {
    background-color: unset !important;
  }
`

const StyledRoleIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.fontSize.px10}rem;
  color: ${({ theme }) => theme.colors.iconGrayDark};

  svg {
    width: ${({ theme }) => theme.fontSize.px10}rem;
    height: ${({ theme }) => theme.fontSize.px10}rem;
  }
`

const StyledColorDot = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color ?? theme.colors.metalGray};
  flex-shrink: 0;
`
